import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import Landing from './pages/Landing';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Wins from './pages/Wins';
import Goals from './pages/Goals';
import BragDocGenerator from './pages/BragDocGenerator';
import CareerCoach from './pages/CareerCoach';
import WeeklyReview from './pages/WeeklyReview';
import MyDocuments from './pages/MyDocuments';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PaymentSuccess from './pages/PaymentSuccess';
import SubscriptionManagement from './pages/SubscriptionManagement';
import Profile from './pages/Profile';
import Pricing from './pages/Pricing';
import PerformanceReviewReadiness from './pages/PerformanceReviewReadiness';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import {
  SoftwareEngineers,
  Nurses,
  Teachers,
  SalesProfessionals,
  MarketingManagers,
  ProjectManagers,
  HRProfessionals,
  Accountants,
  CustomerService,
  DataScientists
} from './pages/tips';
import { captureUtmsFromLanding } from './lib/utm';

// Keep Render backend warm: pings every 10 minutes to prevent cold starts
// (free tier spins down after 15min idle).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function useKeepAlive() {
  useEffect(() => {
    const ping = () => fetch(`${BACKEND_URL}/api/health`).catch(() => {});
    ping();
    const interval = setInterval(ping, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
}

function AppRouter() {
  const location = useLocation();

  // Capture UTM params on every landing / route change. First-touch wins.
  useEffect(() => {
    captureUtmsFromLanding();
  }, [location.pathname, location.search]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        {/* Public routes: single landing, single auth form, one contact page. */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Login />} />
        <Route path="/register" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />

        {/* Pricing kept as a single clean plan page. Pricing.jsx already has
            SHOW_SUBSCRIPTION_TIERS=false so only lifetime + free show — no
            marketplace ladder. ProtectedRoute redirects here for any non-free
            non-lifetime user, so removing this route would break subscription
            enforcement for existing users. */}
        <Route path="/pricing" element={<Pricing />} />

        {/* Payment success page kept so any existing Payhip / Stripe redirects
            land on a real thank-you screen and the gtag conversion pixel in
            public/index.html fires correctly. */}
        <Route path="/payment-success" element={<PaymentSuccess />} />

        {/* High-intent owned conversion asset. This quiz routes readers into the
            £19 / £24 / £27 ladder by urgency instead of dropping them straight
            onto the generic app landing page. */}
        <Route path="/performance-review-readiness" element={<PerformanceReviewReadiness />} />

        {/* Blog and role-tips pages. These stay on the app domain for now.
            Follow-up task moves them to blog.bragbox.ai once DNS is wired.
            Public and cache-friendly, so leaving them here is safe. */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/tips/software-engineers" element={<SoftwareEngineers />} />
        <Route path="/tips/nurses" element={<Nurses />} />
        <Route path="/tips/teachers" element={<Teachers />} />
        <Route path="/tips/sales-professionals" element={<SalesProfessionals />} />
        <Route path="/tips/marketing-managers" element={<MarketingManagers />} />
        <Route path="/tips/project-managers" element={<ProjectManagers />} />
        <Route path="/tips/hr-professionals" element={<HRProfessionals />} />
        <Route path="/tips/accountants" element={<Accountants />} />
        <Route path="/tips/customer-service" element={<CustomerService />} />
        <Route path="/tips/data-scientists" element={<DataScientists />} />

        {/* Most marketplace / PDF-guide sales pages retired 2026-07-08 per the
            pivot audit. Any inbound traffic to these paths now lands on the
            simple app landing page via the wildcard route below.
            Retired paths were: /performance-review-rescue,
            /performance-review-for-remote-workers,
            /promotion-packet-template, /free-guide, /thank-you-guide, /score,
            /pricing. Payhip products still work standalone at
            payhip.com/b/{sku} and the /payment-success route above still
            tracks conversions. */}

        {/* Logged-in workspace. /app is the canonical route. /dashboard,
            /wins, /goals, /brag-doc/new, /coach, /weekly-review, /documents,
            /subscription, /profile, /admin continue to work individually
            for existing sessions and deep links. */}
        <Route path="/app" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/wins" element={
          <ProtectedRoute>
            <Wins />
          </ProtectedRoute>
        } />
        <Route path="/goals" element={
          <ProtectedRoute>
            <Goals />
          </ProtectedRoute>
        } />
        <Route path="/brag-doc/new" element={
          <ProtectedRoute>
            <BragDocGenerator />
          </ProtectedRoute>
        } />
        <Route path="/app/export" element={
          <ProtectedRoute>
            <BragDocGenerator />
          </ProtectedRoute>
        } />
        <Route path="/coach" element={
          <ProtectedRoute>
            <CareerCoach />
          </ProtectedRoute>
        } />
        <Route path="/weekly-review" element={
          <ProtectedRoute>
            <WeeklyReview />
          </ProtectedRoute>
        } />
        <Route path="/documents" element={
          <ProtectedRoute>
            <MyDocuments />
          </ProtectedRoute>
        } />
        <Route path="/subscription" element={
          <ProtectedRoute>
            <SubscriptionManagement />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />

        {/* Retired marketplace paths redirect home so old inbound links still
            land somewhere useful. Everything else gets a real 404 page. */}
        {['/performance-review-rescue', '/performance-review-for-remote-workers',
          '/promotion-packet-template', '/free-guide', '/thank-you-guide', '/score'].map((path) => (
          <Route key={path} path={path} element={<Navigate to="/" replace />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster position="bottom-right" />
    </>
  );
}

function App() {
  useKeepAlive();
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
